import React from 'react';
import { motion } from 'framer-motion';
import { Download, Mail, Github, Linkedin } from 'lucide-react';

const Hero: React.FC = () => {
  return (
    <section id="home" className="section-padding pt-24 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900"></div>
      
      {/* Animated Background Shapes */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Large gradient circles */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-400/20 to-primary-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        {/* Floating geometric shapes */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-br from-yellow-400/10 to-orange-400/10 rounded-lg rotate-12 animate-float"></div>
        <div className="absolute top-40 right-32 w-24 h-24 bg-gradient-to-br from-green-400/10 to-teal-400/10 rounded-full animate-float" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-32 w-20 h-20 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-lg -rotate-12 animate-float" style={{ animationDelay: '3s' }}></div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      <div className="container-max relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <p className="text-primary-600 dark:text-primary-400 font-medium">Hello, I'm</p>
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white">
                Samir Ahmed Talkal
              </h1>
              <h2 className="text-xl md:text-3xl font-semibold gradient-text">
                AI Engineer | LLM &amp; Agentic Systems
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-lg">
                Based in Bengaluru, India, I specialize in enterprise-grade AI agents, LLMs, and RAG pipelines.
                With 3+ years of experience across Python, FastAPI, LangChain, and LangGraph, I build
                production-ready conversational AI systems with a focus on agent evaluation and observability.
              </p>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              <a
                href="https://github.com/SamirTalkal"
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-white/80 dark:bg-dark-700/80 backdrop-blur-sm hover:bg-primary-100 dark:hover:bg-primary-700 text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Github size={20} />
              </a>
              <a
                href="https://www.linkedin.com/in/samir-ahmed-talkal/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-white/80 dark:bg-dark-700/80 backdrop-blur-sm hover:bg-primary-100 dark:hover:bg-primary-700 text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Linkedin size={20} />
              </a>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:samirtalkal@gmail.com"
                className="btn-primary inline-flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <Mail size={18} className="mr-2" />
                Get In Touch
              </a>
              <a
                href="/SamirTalkal_Resume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <Download size={18} className="mr-2" />
                Download Resume
              </a>
            </div>
          </motion.div>

          {/* Right Column - Image/Illustration */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex justify-center lg:justify-end"
          >
            <div className="relative">
              {/* Profile Image */}
              <div className="w-80 h-80 rounded-full overflow-hidden border-4 border-white dark:border-dark-700 shadow-2xl relative z-10">
                <img
                  src="/profile-image.JPG"
                  alt="Samir Ahmed Talkal"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to initials if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.className = 'w-80 h-80 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center border-4 border-white dark:border-dark-700 shadow-2xl relative z-10';
                      parent.innerHTML = '<div class="text-white text-6xl font-bold">ST</div>';
                    }
                  }}
                />
              </div>
              
              {/* Enhanced floating elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full animate-bounce-slow shadow-lg"></div>
              <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-br from-green-400 to-teal-400 rounded-full animate-bounce-slow shadow-lg" style={{ animationDelay: '1s' }}></div>
              <div className="absolute top-1/2 -left-8 w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full animate-bounce-slow shadow-lg" style={{ animationDelay: '2s' }}></div>
              
              {/* Glow effect behind image */}
              <div className="absolute inset-0 w-80 h-80 bg-gradient-to-br from-primary-400/30 to-purple-400/30 rounded-full blur-2xl -z-10"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 